import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { IntlProvider, IntlShape } from 'react-intl';
import { getLocale } from '../locales';
import { getMessages } from '../getMessages';
import "./Registration.scss";
import Logo from '../app/Logo.react';
import { defaultOptions } from '../app/auth.js';
import Auth0Lock from 'auth0-lock';
import Auth0Variables from '../app/auth0-variables';
import { displayError } from '../app/errorDisplayer';
import { isEmbeddedLogin } from '../../outlook/dialog';
import deepCopy from 'deep-copy';
import EventTracker from '../app/EventTracker';
import { expiredTokenHash } from '../../../packages/extension/src/background/Token';
import Deferred from '../fw/deferred';
import { LoadingLine } from "../loadingLine/renderLoadingPanel.react";
import fetch from "node-fetch";
import auth0 from "auth0-js";
import "./RegistrationForm.scss";
import initRelayLayer from "../initRelayLayer";
import { sendToExtension } from "../app/ExtensionCommunicate";
import config from '../../common/config';

/* eslint react/jsx-sort-props: 0 */

export const messages = defineMessages({
    newsletter: { id: 'registration.newsletter', defaultMessage: 'Receive monthly newsletter' },
    agree1: { id: 'registration.signupTerm1', defaultMessage: 'I agree to the' },
    agree2: { id: 'registration.signupTerm2', defaultMessage: 'license agreement' },
    // Login form
    emailLoginTitle: { id: 'login.emailLoginTitle', defaultMessage: 'Login with email' },
    loginExpired: { id: 'login.loginExpired', defaultMessage: 'Session has expired. Please log in again.' },
    emailLabel: { id: 'login.emailLabel', defaultMessage: 'Email' },
    invalidEmail: { id: 'login.invalidEmail', defaultMessage: 'Invalid email address' },
    passwordLabel: { id: 'login.passwordLabel', defaultMessage: 'Password' },
    invalidPassword: { id: 'login.invalidPassword', defaultMessage: 'Invalid password' },
    loginError: { id: 'login.loginError', defaultMessage: 'An error occurred during login' },
    loginButton: { id: 'login.loginButton', defaultMessage: 'Login' },
    loggingIn: { id: 'login.loggingIn', defaultMessage: 'Logging in...' },
    forgotPassword: { id: 'login.forgotPassword', defaultMessage: 'Forgot your password?' },
    // External login
    externalServiceTitle: { id: 'login.externalServiceTitle', defaultMessage: 'Login with external service' },
    googleLogin: { id: 'login.googleLogin', defaultMessage: 'Login with Google' },
    microsoftLogin: { id: 'login.microsoftLogin', defaultMessage: 'Login with Microsoft' },
    // Registration link
    noAccountYet: { id: 'login.noAccountYet', defaultMessage: "Don't have an account yet?" },
    registerLink: { id: 'login.registerLink', defaultMessage: 'Register' },
    // Redirecting state
    loggingInState: { id: 'login.loggingInState', defaultMessage: 'Logging in...' },
    // Default announcement
    welcomeBack: { id: 'login.welcomeBack', defaultMessage: 'Welcome back!' },
    loginToAccess: { id: 'login.loginToAccess', defaultMessage: 'Login to your account to access the world of integrations.' },
    hundredsOfApps: { id: 'login.hundredsOfApps', defaultMessage: 'Hundreds of different connections and application add-ons.' },
});

let loginPromise;
export function login() {
    loginPromise = new Deferred();

    ReactDOM.render(
        <IntlProvider locale={getLocale()} messages={getMessages()}>
            <Login />
        </IntlProvider>,
        document.getElementById('app')
    );

    return loginPromise.promise;
}

interface Login_Props {
 intl: IntlShape;
}

class Login_ extends React.Component<Login_Props> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
    };

    static MAX_STEPS = 4;
    constructor() {
        super();

        this.state = {
            step: 1,
            apps: "",
            newsletter: true,
            email: "",
            password: "",
            emailError: false,
            passwordError: false,
            loginError: null,
            isLoading: false,
            isRedirecting: false,
            showPassword: false,
            announcement: null,
            announcementLoading: true
        };

        this.skipInstallStep = false;
        this.authLock = null;
        
        this.handleChange = this.handleChange.bind(this);
        this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
        this.handleLogin = this.handleLogin.bind(this);
    }

    componentDidMount() {
        this.initAuth();
        this.fetchAnnouncement();
        this.checkAuthenticationState();
    }

    checkAuthenticationState() {
        // Check if we were redirected back with authentication results
        if (window.location.hash && window.location.hash.includes('access_token')) {
            this.setState({ isLoading: true, isRedirecting: true });
            this.auth0Client.parseHash((err, authResult) => {
                if (authResult && authResult.accessToken && authResult.idToken) {
                    this.setSession(authResult);
                } else if (err) {
                    this.setState({
                        isLoading: false,
                        isRedirecting: false,
                        loginError: err.description || "An error occurred during login verification",
                    });
                    console.error(err);
                }
            });
        }
    }

    setSession(authResult) {
        this.auth0Client.client.userInfo(authResult.accessToken, (err, profile) => {
            if (err) {
                displayError(err);
                this.setState({ isLoading: false, isRedirecting: false });
                return;
            }

            EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
                event: 'authentication',
                type: 'application'
            });

            // Set tokens and user profile in local storage
            localStorage.setItem('profile', JSON.stringify(profile));
            localStorage.setItem('userToken', authResult.idToken);

            // Send token to extension if available
            sendToExtension({
                message: 'dativery-set-token',
                data: {token: authResult.idToken}
            });

            // Clear the URL hash
            if (Login_.isLoginExpired()) {
                history.pushState("", document.title, window.location.pathname + window.location.search);
            }

            this.setState({ isRedirecting: true }, () => {
                // Use setTimeout to ensure the login state is saved before redirect
                setTimeout(() => {
                    this.finishRegistrion();
                }, 50);
            });
        });
    }

    static isLoginExpired() {
        return window.location.hash === expiredTokenHash;
    }

    handleChange(event) {
        const { name, value } = event.target;
        this.setState({ [name]: value });
    }

    togglePasswordVisibility() {
        this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
    }

    handleLogin(event) {
        event.preventDefault();
        
        this.setState({
            emailError: false,
            passwordError: false,
            loginError: null,
            isLoading: true,
        });

        // Store form values to prevent them from being cleared
        const { email, password } = this.state;
        const redirectPath = this.getRedirectPath() || window.location.pathname;

        this.auth0Client.login({
            realm: "Username-Password-Authentication",
            username: email,
            password: password,
            responseType: "token id_token",
            redirectUri: window.location.origin + "/login?pathname=" + encodeURIComponent(redirectPath),
        }, (err, authResult) => {
            if (err) {
                this.setState({
                    isLoading: false,
                    loginError: err.description || "An error occurred during login",
                });
                return;
            }
            
            // If auth0 returns the result directly (not via redirect)
            if (authResult && authResult.accessToken) {
                // Set redirecting state immediately to prevent UI flickering
                this.setState({ isRedirecting: true });
                this.setSession(authResult);
            }
            // Otherwise, the page will be redirected and results handled by checkAuthenticationState
        });
    }

    handleSocialLogin = (connection) => {
        this.setState({ isLoading: true, isRedirecting: true });
        const redirectPath = this.getRedirectPath() || window.location.pathname;
        this.auth0Client.authorize({
            connection,
            responseType: "token id_token",
            redirectUri: window.location.origin + "/login?pathname=" + encodeURIComponent(redirectPath),
        });
    };

    fetchAnnouncement = () => {
        fetch(config.GLOBAL_API_URL + '/login-announcement')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                this.setState({
                    announcement: data,
                    announcementLoading: false
                });
            })
            .catch(error => {
                console.error('Error fetching announcement:', error);
                this.setState({ announcementLoading: false });
            });
    };

    render() {
        // If we're in the redirecting state, show a simple loading screen
        if (this.state.isRedirecting) {
            return (
                <div className="Registration Login">
                    <header><a href="https://www.dativery.com"><Logo color /></a></header>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '60vh',
                        padding: '2rem'
                    }}>
                        <LoadingLine />
                        <h1 style={{ marginTop: '2rem' }}>{this.props.intl.formatMessage(messages.loggingInState)}</h1>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="Registration Login">
                <header><a href="https://www.dativery.com"><Logo color /></a></header>
                {this.renderLogin()}
            </div>
        );
    }

    initAuth() {
        const redirectPath = this.getRedirectPath() || window.location.pathname;
        // Initialize Auth0 WebAuth for programmatic login
        this.auth0Client = new auth0.WebAuth({
            domain: Auth0Variables.AUTH0_DOMAIN,
            clientID: Auth0Variables.AUTH0_CLIENT_ID,
            responseType: "token id_token",
            redirectUri: window.location.origin + "/login?pathname=" + encodeURIComponent(redirectPath),
            scope: "openid profile email"
        });

        // Also initialize Auth0Lock as fallback
        let options = deepCopy(defaultOptions);
        options.auth = {
            ...options.auth,
            redirectUrl: window.location.origin + "/login?pathname=" + encodeURIComponent(redirectPath),
            responseType: 'token',
            params: {
                scope: 'openid profile email'
            }
        };

        options.allowSignUp = false;
        options.allowForgotPassword = true;
        options.closable = true;

        if (isEmbeddedLogin()) {
            options.container = undefined;
            options.theme.logo = defaultOptions.theme.logo;
        }
        else {
            options.auth.redirect = false;
        }

        this.authLock = new Auth0Lock(
            Auth0Variables.AUTH0_CLIENT_ID,
            Auth0Variables.AUTH0_DOMAIN,
            options
        );

        // Add listener for authenticated event
        this.authLock.on("authenticated", (authResult) => {
            this.setState({ isRedirecting: true });
            this.setSession(authResult);
        });
    }
    showLockWidget = () => {
        if (this.authLock) {
            // Configure Auth0Lock to show the forgot password screen directly
            const forgotPasswordOptions = {
                initialScreen: 'forgotPassword',
                allowLogin: false,
                allowSignUp: false,
                closable: true
            };
            this.authLock.show(forgotPasswordOptions);
        }
    };

    getRedirectPath() {
        if (window.location.query && window.location.query.pathname) {
            return window.location.query.pathname;
        }
        if (window.location.hash && window.location.hash.indexOf("#pathname") === 0) {
            return decodeURI(window.location.hash.substring(10));
        }
        if (window.location.search) {
            const params = new URLSearchParams(window.location.search);
            if (params.get("pathname")) {
                return params.get("pathname");
            }
        }
        try {
            const savedPath = sessionStorage.getItem('dativery-redirect-path');
            if (savedPath) {
                return savedPath;
            }
        } catch (e) {
            // ignore
        }
        return null;
    }

    renderLogin() {
        const { announcement, announcementLoading } = this.state;
        let redirectPath = this.getRedirectPath();
        // Fallback: if getRedirectPath() returns null but the current URL is an invitation link,
        // use the pathname directly (Login page is rendered via ReactDOM.render without changing the URL)
        if (!redirectPath && window.location.pathname.startsWith('/invitation/')) {
            redirectPath = window.location.pathname;
        }
        const registerLink = redirectPath ? `/registration#pathname=${redirectPath}` : "/registration";

        return (
            <div
                style={{
                    display: "flex",
                    minHeight: "100vh",
                    width: "100%",
                    marginTop: "-3rem",
                }}
            >
                <main style={{ flex: "1", display: "flex", backgroundColor: "#f9fafb" }}>
                    <div style={{ display: "flex", width: "100%" }}>
                        <div style={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <div style={{ width: "100%", maxWidth: "448px" }}>
                                <div>
                                    <div className="register-registration-container">
                                        <div className="register-form-section register-email-section">
                                            <h2>{this.props.intl.formatMessage(messages.emailLoginTitle)}</h2>
                                            {Login_.isLoginExpired() ? (
                                                <div className="error-banner">
                                                    {this.props.intl.formatMessage(messages.loginExpired)}
                                                </div>
                                            ) : ''}

                                            <form onSubmit={this.handleLogin}>
                                                <div className="register-form-group">
                                                    <label htmlFor="email">{this.props.intl.formatMessage(messages.emailLabel)}</label>
                                                    <div className="register-input-wrapper">
                                                        <span className="register-input-icon">
                                                            <i className="fa fa-envelope" aria-hidden="true"></i>
                                                        </span>
                                                        <input
                                                            type="email"
                                                            id="email"
                                                            name="email"
                                                            value={this.state.email}
                                                            onChange={this.handleChange}
                                                            required
                                                            className={this.state.emailError ? "error-input" : ""}
                                                        />
                                                    </div>
                                                    {this.state.emailError && (
                                                        <p className="error-message">
                                                            {typeof this.state.emailError === "string" ? this.state.emailError : this.props.intl.formatMessage(messages.invalidEmail)}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="register-form-group">
                                                    <label htmlFor="password">{this.props.intl.formatMessage(messages.passwordLabel)}</label>
                                                    <div className="register-input-wrapper">
                                                        <span className="register-input-icon">
                                                            <i className="fa fa-lock" aria-hidden="true"></i>
                                                        </span>
                                                        <input
                                                            type={this.state.showPassword ? "text" : "password"}
                                                            id="password"
                                                            name="password"
                                                            value={this.state.password}
                                                            onChange={this.handleChange}
                                                            required
                                                            className={this.state.passwordError ? "error-input" : ""}
                                                        />
                                                        <button 
                                                            type="button" 
                                                            className="register-input-icon-right" 
                                                            onClick={this.togglePasswordVisibility}
                                                        >
                                                            <i 
                                                                className={`fa ${this.state.showPassword ? "fa-eye-slash" : "fa-eye"}`} 
                                                                aria-hidden="true"
                                                            ></i>
                                                        </button>
                                                    </div>
                                                    {this.state.passwordError && (
                                                        <p className="error-message">
                                                            {typeof this.state.passwordError === "string" ? this.state.passwordError : this.props.intl.formatMessage(messages.invalidPassword)}
                                                        </p>
                                                    )}
                                                </div>

                                                {this.state.loginError && <div className="error-banner">{this.state.loginError}</div>}

                                                <button
                                                    type="submit"
                                                    className={`register-btn register-submit-btn ${this.state.isLoading ? "register-disabled-btn" : ""}`}
                                                    disabled={this.state.isLoading}
                                                >
                                                    {this.state.isLoading ? this.props.intl.formatMessage(messages.loggingIn) : this.props.intl.formatMessage(messages.loginButton)}
                                                </button>
                                                
                                                <div className="forgot-password-link" style={{ marginTop: "1rem", textAlign: "center" }}>
                                                    <a 
                                                        href="javascript:void(0);" 
                                                        onClick={this.showLockWidget}
                                                    >
                                                        {this.props.intl.formatMessage(messages.forgotPassword)}
                                                    </a>
                                                </div>
                                            </form>
                                        </div>

                                        <div className="register-form-section register-external-section">
                                            <h2>{this.props.intl.formatMessage(messages.externalServiceTitle)}</h2>
                                            <button
                                                type="button"
                                                className="register-btn register-external-btn register-google-btn"
                                                onClick={() => this.handleSocialLogin("google-oauth2")}
                                                disabled={this.state.isLoading}
                                            >
                                                <span className="register-btn-icon">G</span> {this.props.intl.formatMessage(messages.googleLogin)}
                                            </button>
                                            <button
                                                type="button"
                                                className="register-btn register-external-btn register-microsoft-btn"
                                                onClick={() => this.handleSocialLogin("windowslive")}
                                                disabled={this.state.isLoading}
                                            >
                                                <span className="register-btn-icon">M</span> {this.props.intl.formatMessage(messages.microsoftLogin)}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="register-login-link">
                                        <p>
                                            {this.props.intl.formatMessage(messages.noAccountYet)} <a href={registerLink}>{this.props.intl.formatMessage(messages.registerLink)}</a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ width: "50%", position: "relative" }}>
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    backgroundImage: announcement 
                                        ? `url('${announcement.backgroundImage}')`
                                        : "url('https://img.freepik.com/free-photo/showing-cart-trolley-shopping-online-sign-graphic_53876-133967.jpg')",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    filter: "brightness(50%)",
                                }}
                            ></div>
                            <div
                                style={{
                                    position: "absolute",
                                    marginInline: "auto",
                                    width: "fit-content",
                                    inset: "0",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    maxWidth: "70em",
                                    padding: "8em",
                                    textAlign: "left",
                                    overflowY: "auto",
                                    lineHeight: "1.4",
                                }}
                            >
                                {announcementLoading ? (
                                    <LoadingLine />
                                ) : announcement ? (
                                    <div style={{backgroundColor: "rgba(0, 0, 0, 0.4)", padding: "2em", borderRadius: "8px"}}>
                                        <h2
                                            style={{
                                                fontSize: "1.875rem",
                                                fontWeight: "bold",
                                                marginBottom: "1.5em",
                                                
                                            }}
                                        >
                                            {announcement.title}
                                        </h2>
                                        <div 
                                            className="registration-announcement-text"
                                            style={{ 
                                                marginBottom: "1.5em"
                                            }}
                                            dangerouslySetInnerHTML={{ __html: announcement.body }}
                                        />
                                        {announcement.link && (
                                            <div style={{textAlign: "center"}}>
                                                <a 
                                                    href={announcement.link}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: "inline-block",
                                                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                        color: "white",
                                                        marginTop: "1.5em",
                                                        padding: "0.5rem 1rem",
                                                        borderRadius: "0.25rem",
                                                        textDecoration: "none",
                                                        fontWeight: "500",
                                                        backdropFilter: "blur(4px)",
                                                        border: "1px solid rgba(255, 255, 255, 0.3)",
                                                        transition: "all 0.3s ease"
                                                    }}
                                                    onMouseOver={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)"}
                                                    onMouseOut={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                                                >
                                                    {announcement.linkText || "Více informací"}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <h2
                                            style={{
                                                fontSize: "1.875rem",
                                                fontWeight: "bold",
                                                marginBottom: "1rem",
                                            }}
                                        >
                                            {this.props.intl.formatMessage(messages.welcomeBack)}
                                        </h2>
                                        <p style={{ marginBottom: "0.5rem", textShadow: "1px 1px 1px rgba(0, 0, 0, 0.5)" }}>
                                            {this.props.intl.formatMessage(messages.loginToAccess)}
                                        </p>
                                        <p style={{ marginBottom: "0.5rem", textShadow: "1px 1px 1px rgba(0, 0, 0, 0.5)" }}>
                                            {this.props.intl.formatMessage(messages.hundredsOfApps)}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    finishRegistrion() {
        const redirectPath = this.getRedirectPath();
        
        if (redirectPath) {
            try {
                sessionStorage.removeItem('dativery-redirect-path');
            } catch (e) {
                // ignore
            }
            window.location = window.location.origin + redirectPath;
        } else {
            window.location = window.location.origin + "/s/";
        }
        
        loginPromise.resolve();
    }
}
let Login = injectIntl(Login_);
